import { BoundedContent, Box } from '@vp/swan';
import { MPVIDS_WITH_TIERED_OPTIONS } from '@99designs/design-services-common';
import { useInVistaExperimentVariant } from '@99designs/experiments';
import { AuthenticatedApolloProvider } from '@99designs/graph-utils';
import { __ } from '@99designs/i18n';
import { TrustpilotWidget } from '@99designs/reviews';
import { ServiceDetailsPageDataQuery } from '../../graphql';
import { FAQSection } from '../FAQSection';
import { Header } from '../Header';
import { HowItWorks } from '../HowItWorks';
import { Inclusions } from '../Inclusions';
import { Inspiration } from '../Inspiration';
import { Personalisation } from '../RecordPersonalisation';
import { TierLayout as TieringExperimentLayout } from '../TieringExperiment/redesign/TierLayout';

interface MainContentProps {
    locale: string;
    serviceMpvId: string;
    productName: string;
    serviceDetails: ServiceDetailsPageDataQuery['serviceDetailPage'];
    expertServices: ServiceDetailsPageDataQuery['expertServices'];
    workId: string;
    routeSpecifiedMpvId: string;
    printMpvId?: string;
    price: ServiceDetailsPageDataQuery['expertServices'][0]['price'];
    isProductDesignLiveEnabled: boolean;
    environment: string;
}

export const MainContent = ({
    locale,
    serviceMpvId,
    productName,
    serviceDetails,
    routeSpecifiedMpvId,
    workId,
    price,
    isProductDesignLiveEnabled,
    environment,
    expertServices,
    printMpvId,
}: MainContentProps) => {
    const inVariant = useInVistaExperimentVariant('design_services_sdp_turnaround_time');
    const lowercaseMpvId = routeSpecifiedMpvId.toLowerCase();

    // get the properties for the serviceProduct and the expert service
    const expertService = expertServices.find(
        (service) => service.serviceProduct.mpvId === serviceMpvId
    );
    const properties = expertService?.serviceProduct.properties || [];

    if (
        locale === 'en-US' &&
        MPVIDS_WITH_TIERED_OPTIONS[locale].includes(lowercaseMpvId) &&
        (inVariant('variant_24_hour') || inVariant('variant_3_day'))
    ) {
        return (
            <TieringExperimentLayout
                mpvId={lowercaseMpvId}
                productName={productName}
                locale={locale}
                heroImages={serviceDetails.heroImages}
            />
        );
    }

    const showTrustpilotWidget = locale === 'en-AU' && serviceMpvId === 'careDesign';

    return (
        <>
            <Box mb="between-sections">
                <Header
                    sectionTitle={__('Design Services')}
                    name={productName}
                    heroImages={serviceDetails.heroImages}
                    description={serviceDetails.description}
                    cta={serviceDetails.primaryCta}
                    renderInclusions={() => (
                        <Inclusions
                            title={__('What you get:')}
                            inclusions={serviceDetails.inclusions}
                            properties={properties}
                        />
                    )}
                    price={price}
                    routeSpecifiedMpvId={routeSpecifiedMpvId}
                    printMpvId={printMpvId}
                    workId={workId}
                    isDesignLiveEnabled={isProductDesignLiveEnabled}
                    environment={environment}
                    locale={locale}
                    expertService={expertService ? { isLegacy: expertService.isLegacy } : undefined}
                />
            </Box>

            <Box mb="between-sections">
                <HowItWorks
                    title={serviceDetails.overview.title}
                    description={serviceDetails.overview.descriptionHtml}
                    image={{
                        url: serviceDetails.overview.image.cloudinaryImage.cloudinaryUrl,
                        alt: serviceDetails.overview.image.altTextOverride,
                    }}
                />
            </Box>
            {showTrustpilotWidget && (
                <Box mb="between-sections">
                    <TrustpilotWidget locale={locale} />
                </Box>
            )}
            {serviceDetails.inspiration && (
                <Box mb="between-sections">
                    <BoundedContent>
                        <Inspiration
                            title={serviceDetails.inspiration.title}
                            subTitle={serviceDetails.inspiration.subtitle || undefined}
                            images={serviceDetails.inspiration.designs.map((design) => {
                                return {
                                    designerAttribution: {
                                        designerName: design.designer.designerName,
                                        avatarURL: design.designer.avatarUrl,
                                    },
                                    src: design.image.cloudinaryImage.cloudinaryUrl,
                                    alt: design.image.altTextOverride,
                                };
                            })}
                        />
                    </BoundedContent>
                </Box>
            )}
            <Box mb="between-sections">
                <FAQSection faqs={serviceDetails.faqs} />
            </Box>
            <AuthenticatedApolloProvider
                locale={locale}
                endpoint={
                    process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT || 'https://graph.es.vpsvc.com/graph'
                }
            >
                <Personalisation mpvId={routeSpecifiedMpvId} />
            </AuthenticatedApolloProvider>
        </>
    );
};
